import { useEffect, useMemo, useRef, type PointerEvent, type RefObject } from "react";
import { DragGesture } from "../drag/gesture.ts";
import { placeGhost } from "../drag/ghost.tsx";
import { dropOf, dropTargetAt } from "../drag/targets.ts";
import type { ToolBelt } from "./store.ts";

/**
 * Dragging the belt's slots about (ADR-142 decision 3b), on the bag's gesture (`drag/gesture.ts`): a press on a slot
 * holding a tool becomes a drag once it moves more than 8 CSS pixels inside 500 ms; a press released without moving is a
 * click, which takes the tool in hand or puts it away. Released over another slot of the belt the two swap, the tool in
 * hand staying in hand; released anywhere else the tool comes off the belt. Escape, a lost window and a cancelled
 * pointer end the drag with nothing changed. A touch never drags from the belt: the chip's fan takes a tap. The click that
 * follows a drop is swallowed, so a drop never takes the slot it lands on in hand. The ghost follows the pointer, placed on
 * pointer events only, so no frame allocates. Shift is not asked: the belt holds a few tools, taken off by a drag at
 * once.
 */

/**
 * What the belt draws a drag with: the press handler for its slots, the ghost's element, whether the click now arriving
 * ends a drop and must be swallowed, and a way to place the ghost as it is first drawn.
 */
export interface BeltDrag {
  readonly onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  readonly ghost: RefObject<HTMLDivElement | null>;
  readonly swallow: () => boolean;
  readonly placeNow: () => void;
}

/**
 * Drags slots of the belt about.
 *
 * @param belt The page's tool belt, or null where there is none.
 * @returns What the belt draws the drag with.
 */
export function useBeltDrag(belt: ToolBelt | null): BeltDrag {
  const gesture = useMemo(() => new DragGesture(), []);
  const ghost = useRef<HTMLDivElement>(null);
  const swallowed = useRef(false);
  const pointer = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (belt === null) {
      return undefined;
    }
    /**
     * The belt slot under a point, or null off the belt.
     *
     * @param x The point's x.
     * @param y The point's y.
     * @returns The slot, from 0.
     */
    const beltAt = (x: number, y: number): number | null => {
      const target = dropTargetAt(x, y);
      return target !== null && target.surface === "belt" ? target.index : null;
    };
    /**
     * Follows the pointer: starts the drag once the press has moved far enough in time, then moves the ghost and marks
     * the slot under the pointer.
     *
     * @param event The move.
     */
    const moved = (event: globalThis.PointerEvent): void => {
      pointer.current.x = event.clientX;
      pointer.current.y = event.clientY;
      if (gesture.move(event.pointerId, event.clientX, event.clientY, performance.now())) {
        const source = gesture.active;
        if (source === null || !belt.canArrange()) {
          gesture.cancel();
          return;
        }
        belt.setDragging(source.index);
      }
      if (gesture.active !== null) {
        placeGhost(ghost.current, event.clientX, event.clientY);
        const over = beltAt(event.clientX, event.clientY);
        belt.setOver(over === null || over === gesture.active.index ? null : { index: over, tone: "positive" });
      }
    };
    /**
     * Drops the drag where the pointer is released, and swallows the click that follows.
     *
     * @param event The release.
     */
    const released = (event: globalThis.PointerEvent): void => {
      const source = gesture.release(event.pointerId);
      if (source === null) {
        return;
      }
      const over = beltAt(event.clientX, event.clientY);
      belt.setDragging(null);
      belt.setOver(null);
      if (over === null) {
        belt.clear(source.index);
      } else if (over !== source.index) {
        belt.swap(source.index, over);
      }
      swallowed.current = true;
      setTimeout(() => {
        swallowed.current = false;
      }, 0);
    };
    /**
     * Ends a drag with nothing changed.
     */
    const cancelled = (): void => {
      if (gesture.cancel()) {
        belt.setDragging(null);
        belt.setOver(null);
      }
    };
    /**
     * Escape ends a drag before anything else hears it.
     *
     * @param event The key.
     */
    const keyed = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && gesture.active !== null) {
        event.preventDefault();
        event.stopImmediatePropagation();
        cancelled();
      }
    };
    window.addEventListener("pointermove", moved);
    window.addEventListener("pointerup", released);
    window.addEventListener("pointercancel", cancelled);
    window.addEventListener("blur", cancelled);
    window.addEventListener("keydown", keyed, { capture: true });
    return () => {
      window.removeEventListener("pointermove", moved);
      window.removeEventListener("pointerup", released);
      window.removeEventListener("pointercancel", cancelled);
      window.removeEventListener("blur", cancelled);
      window.removeEventListener("keydown", keyed, { capture: true });
      cancelled();
    };
  }, [gesture, belt]);

  return useMemo(
    (): BeltDrag => ({
      onPointerDown: (event) => {
        if (belt === null || event.button !== 0 || event.pointerType === "touch" || !belt.canArrange()) {
          return;
        }
        const anchor = dropOf(event.target as Element);
        if (anchor === null || anchor.surface !== "belt" || belt.getSnapshot().record.slots[anchor.index] == null) {
          return;
        }
        gesture.press(anchor, event.pointerId, event.clientX, event.clientY, performance.now());
      },
      ghost,
      swallow: () => swallowed.current,
      placeNow: () => placeGhost(ghost.current, pointer.current.x, pointer.current.y),
    }),
    [belt, gesture],
  );
}
